import React from "react";
import {
  AppBar,
  Box,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  Divider,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { UserService } from "../../../../services/UserService";
import { RAG_ENABLED } from "../../../../config/featureFlags";
import { ADMIN_PATHS } from "../../../../config/adminRoutes";

const drawerWidth = 260;

// Header geometry - keep in sync with the content offset in App.scss / --header-height.
const BRAND_H = 52; // tier 1: brand strip
const RAIL_H = 48; // tier 2: nav rail
const MOBILE_H = 56; // single bar on small screens

const TAGLINE = "International, national & regional scholar awards";
const TAGLINE_FULL =
  "A database of international, national and regional scholar awards across STEM, the Liberal Arts, Medicine, and Business";

const pages = [
  { label: "UTA Highlight", path: "/uta_faculty_awards" },
  { label: "Awards", path: "/awards" },
  { label: "Awardees", path: "/awardees" },
  { label: "Organization", path: "/societies" },
  { label: "Correlated Awards", path: "/correlated_awards" },
];

const topLinks = [
  { label: "About", path: "/about" },
  { label: "Tips", path: "/tips" },
];

// The Admin menu, desktop and mobile drawer alike; `show` decides per user. A new admin
// page is one entry here.
const ADMIN_LINKS = [
  // /assessment only exists when the RAG routes are registered, so the link has to
  // share that flag or it would send people to a route that renders nothing.
  { label: "Assessment", path: "/assessment", Icon: FactCheckRoundedIcon, show: ({ isLoggedIn }) => isLoggedIn && RAG_ENABLED },
  { label: "Analyses", path: "/analyses", Icon: InsightsRoundedIcon, show: ({ isLoggedIn }) => isLoggedIn },
  { label: "Data sync", path: ADMIN_PATHS.dataSync, Icon: SyncRoundedIcon, show: ({ isAdmin }) => isAdmin },
  { label: "Update Log", path: "/update-log", Icon: HistoryRoundedIcon, show: ({ isLoggedIn }) => isLoggedIn },
];

// White-on-color helpers (both palettes use a light contrastText).
const VRULE = { width: "1px", height: 20, bgcolor: "rgba(255,255,255,0.28)", flexShrink: 0 };
const ACTION_BTN = {
  fontWeight: 600,
  color: "inherit",
  opacity: 0.9,
  "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.08)" },
};

export default function SideBarComponent() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(false);
  const [adminAnchor, setAdminAnchor] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // UTA Highlight is available to everyone; only the color theme is network-driven.
  const visiblePages = pages;

  const isLoggedIn = UserService.isLoggedIn && !UserService.isUserTokenExpired;
  const isAdmin = isLoggedIn && UserService.isAdmin;
  const adminLinks = ADMIN_LINKS.filter((link) => link.show({ isLoggedIn, isAdmin }));

  const handleLogout = () => {
    UserService.logOut(false);
    navigate("/");
  };

  const handleAdminMenu = (e) => setAdminAnchor(e.currentTarget);
  const closeAdminMenu = () => setAdminAnchor(null);
  const goAdmin = (path) => {
    closeAdminMenu();
    navigate(path);
  };

  // Awards & Awardees share the /awards route, disambiguated by ?type=awardees
  // (the /awardees link redirects there), so the highlight must read the param.
  const awardSearchType = new URLSearchParams(location.search).get("type");
  const isActivePage = (p) => {
    if (p.path === "/awards")
      return (
        (location.pathname === "/awards" && awardSearchType !== "awardees") ||
        location.pathname === "/award" ||
        location.pathname.startsWith("/award/")
      );
    if (p.path === "/awardees")
      return (
        location.pathname === "/awardees" ||
        (location.pathname === "/awards" && awardSearchType === "awardees")
      );
    return (
      location.pathname === p.path || location.pathname.startsWith(p.path + "/")
    );
  };
  const currentTab = visiblePages.findIndex(isActivePage);

  const handleTabChange = (_, newValue) => {
    navigate(visiblePages[newValue].path);
  };

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
        }}
      >
        {/* Tier 1 - brand strip */}
        <Toolbar
          variant="dense"
          sx={{
            minHeight: { xs: MOBILE_H, sm: BRAND_H },
            height: { xs: MOBILE_H, sm: BRAND_H },
            px: { xs: 1.5, md: 3 },
            gap: 1.5,
          }}
        >
          {isSmallScreen && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerOpen}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Tooltip arrow title={TAGLINE_FULL}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Typography
                component="span"
                aria-label="SCHOLARS"
                onClick={() => navigate("/")}
                sx={{
                  fontFamily: "var(--app-font-heading)",
                  fontWeight: 800,
                  fontSize: "1.25rem",
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                SCHOLARS
                <Box component="span" sx={{ color: "secondary.main" }}>
                  .
                </Box>
              </Typography>
              {!isSmallScreen && (
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <Box sx={VRULE} />
                  <Typography noWrap variant="caption" sx={{ opacity: 0.78, fontWeight: 500 }}>
                    {TAGLINE}
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {!isSmallScreen && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {topLinks.map((link) => (
                <Button
                  key={link.label}
                  size="small"
                  sx={ACTION_BTN}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </Button>
              ))}

              {isLoggedIn && (
                <>
                  <Tooltip title="Admin">
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={handleAdminMenu}
                      aria-label="Admin menu"
                      sx={{ ml: 0.5 }}
                    >
                      <AdminPanelSettingsRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={adminAnchor}
                    open={Boolean(adminAnchor)}
                    onClose={closeAdminMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    {adminLinks.map(({ label, path, Icon }) => (
                      <MenuItem key={path} onClick={() => goAdmin(path)}>
                        <ListItemIcon>
                          <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

              <Box sx={{ ...VRULE, mx: 0.75 }} />

              {isLoggedIn ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={handleLogout}
                  sx={{
                    borderColor: "rgba(255,255,255,0.5)",
                    "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  Logout
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate("/login")}
                  sx={{ fontWeight: 700 }}
                >
                  Login
                </Button>
              )}
            </Box>
          )}
        </Toolbar>

        {/* Tier 2 - nav rail (desktop only) */}
        {!isSmallScreen && (
          <Box
            sx={{
              bgcolor: "primary.dark",
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Tabs
              value={currentTab !== -1 ? currentTab : false}
              onChange={handleTabChange}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                minHeight: RAIL_H,
                px: { md: 3 },
                "& .MuiTab-root": {
                  minHeight: RAIL_H,
                  py: 0,
                  px: 2,
                  fontSize: "0.9rem",
                  color: "rgba(255,255,255,0.75)",
                  // Same hover as the tier-1 icons: MUI tabs only react on press (the ripple), which reads as dead on a mouse.
                  transition: "color 0.15s, background-color 0.15s",
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  "&.Mui-selected": { color: "#fff" },
                },
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: 3,
                  bgcolor: "secondary.main",
                },
              }}
            >
              {visiblePages.map((page) => (
                <Tab key={page.label} label={page.label} />
              ))}
            </Tabs>
          </Box>
        )}
      </AppBar>

      {/* Drawer - mobile only; carries nav + utility + admin + auth */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        sx={{ "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" } }}
      >
        <Toolbar sx={{ justifyContent: "flex-end" }}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Toolbar>
        <Divider />
        <Box sx={{ px: 2, mt: 2 }}>
          {visiblePages.map((page) => (
            <Box
              key={page.label}
              onClick={() => {
                navigate(page.path);
                handleDrawerClose();
              }}
              sx={{
                mb: 2,
                fontWeight: 500,
                cursor: "pointer",
                color: isActivePage(page) ? "primary.main" : "text.primary",
              }}
            >
              {page.label}
            </Box>
          ))}

          <Divider sx={{ my: 1.5 }} />
          {topLinks.map((link) => (
            <Box
              key={link.label}
              onClick={() => {
                navigate(link.path);
                handleDrawerClose();
              }}
              sx={{ mb: 2, fontWeight: 500, cursor: "pointer" }}
            >
              {link.label}
            </Box>
          ))}

          {isLoggedIn && (
            <>
              <Divider sx={{ my: 1.5 }} />
              {adminLinks.map(({ label, path }) => (
                <Box
                  key={path}
                  onClick={() => {
                    navigate(path);
                    handleDrawerClose();
                  }}
                  sx={{ mb: 2, fontWeight: 500, cursor: "pointer" }}
                >
                  {label}
                </Box>
              ))}
            </>
          )}

          <Divider sx={{ my: 1.5 }} />
          {isLoggedIn ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                handleLogout();
                handleDrawerClose();
              }}
            >
              Logout
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => {
                navigate("/login");
                handleDrawerClose();
              }}
            >
              Login
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
}
